<template>
	<div class="ep-mail">
		<div class="ep-mail-bar">
			<span class="ep-mail-k">Oggetto</span>
			<span v-if="oggetto" class="ep-mail-subject" v-html="evidenzia(oggetto)"></span>
			<em v-else class="ep-muted">senza oggetto</em>
		</div>
		<div class="ep-mail-paper" :style="maxHeight ? { maxHeight: maxHeight + 'px' } : null">
			<div v-if="oggetto" class="ep-mail-title" v-html="evidenzia(oggetto)"></div>
			<!-- l'HTML del template sta in un iframe sandbox senza allow-scripts: script ed eventi non partono mai -->
			<iframe
				v-if="testoPiano(testo)"
				ref="frame"
				class="ep-mail-frame"
				sandbox="allow-same-origin"
				title="Testo dell'email"
				:srcdoc="srcdoc"
				:style="{ height: altezza + 'px' }"
				@load="misura"
			></iframe>
			<div v-else class="ep-muted ep-mail-empty">Nessun testo in questa lingua</div>
			<div v-if="pulsante" class="ep-mail-cta">
				<span class="ep-mail-button" v-html="evidenzia(pulsante)"></span>
			</div>
		</div>
	</div>
</template>

<script>
import he from 'he';
import { PLACEHOLDER, testoPiano } from './tipologie';

// anteprima "a busta" di un template email: oggetto, testo e pulsante, con i placeholder evidenziati
export default {
	name: 'EmailPreview',
	props: {
		// { oggetto, text, button } di una lingua
		traduzione: { type: Object, default: () => ({}) },
		// i template salvati arrivano dal backend con l'HTML codificato in entità (vedi he.decode in Edit.vue)
		codificato: { type: Boolean, default: false },
		maxHeight: { type: Number, default: 420 },
	},
	data() {
		return { altezza: 40 };
	},
	computed: {
		srcdoc() {
			return `<!doctype html><html><head><meta charset="utf-8"><style>${STILE_FRAME}</style></head><body>${this.evidenziaHtml(this.testo)}</body></html>`;
		},
		oggetto() {
			return this.decodifica(this.traduzione.oggetto);
		},
		testo() {
			return this.decodifica(this.traduzione.text);
		},
		pulsante() {
			return this.decodifica(this.traduzione.button);
		},
	},
	methods: {
		testoPiano,
		decodifica(v) {
			const s = String(v || '');
			return this.codificato ? he.decode(s) : s;
		},
		escape(s) {
			return he.escape(String(s || ''));
		},
		chip(nome) {
			const noto = PLACEHOLDER[nome];
			return `<span class="ep-ph${noto ? '' : ' ep-ph--ko'}" title="${this.escape(noto || "Placeholder non riconosciuto: nell'email resta scritto così")}">[${nome}]</span>`;
		},
		// testo semplice (oggetto, pulsante): escape e placeholder evidenziati
		evidenzia(testo) {
			return this.escape(testo).replace(/\[([a-zA-Z_]+)\]/g, (m, nome) => this.chip(nome));
		},
		// HTML del testo con i placeholder evidenziati solo nei nodi di testo (mai dentro gli attributi,
		// es. href="[urlsito]"). Non e' un sanitizer: la protezione e' la sandbox dell'iframe.
		evidenziaHtml(html) {
			const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
			const radice = doc.body.firstChild;
			const walker = doc.createTreeWalker(radice, NodeFilter.SHOW_TEXT);
			const nodi = [];
			while (walker.nextNode()) nodi.push(walker.currentNode);
			nodi.forEach(nodo => {
				if (!/\[[a-zA-Z_]+\]/.test(nodo.nodeValue)) return;
				const span = doc.createElement('span');
				span.innerHTML = this.evidenzia(nodo.nodeValue);
				nodo.replaceWith(...span.childNodes);
			});
			// i link dell'anteprima non portano da nessuna parte
			radice.querySelectorAll('a').forEach(a => a.removeAttribute('href'));
			return radice.innerHTML;
		},
		// l'iframe prende l'altezza del contenuto (allow-same-origin permette di leggerla; senza allow-scripts dentro non gira nulla)
		misura() {
			const frame = this.$refs.frame;
			const doc = frame && frame.contentDocument;
			if (!doc) return;
			this.altezza = doc.documentElement.scrollHeight;
			// le immagini arrivano dopo: rimisura quando sono caricate
			doc.querySelectorAll('img').forEach(img => {
				if (!img.complete) img.addEventListener('load', () => this.misura(), { once: true });
			});
		},
	},
};

// stile del testo dentro l'iframe (gli stili della pagina non ci arrivano)
const STILE_FRAME = `
html, body { margin: 0; padding: 0; background: transparent; overflow: hidden; }
body { font-family: Roboto, Arial, sans-serif; font-size: 14px; line-height: 1.55; color: #333; word-break: break-word; }
p { margin: 0 0 8px; }
img { max-width: 100%; height: auto; }
a { color: #01579b; text-decoration: underline; }
.ep-ph { display: inline-block; padding: 0 4px; border-radius: 4px; background: #e3edfb; color: #1a4f9c; font-family: monospace; font-size: 0.9em; font-weight: 600; line-height: 1.4; }
.ep-ph--ko { background: #fde7e7; color: #b3261e; }
`;
</script>

<style scoped>
.ep-mail {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.ep-mail-bar {
	display: flex;
	gap: 8px;
	align-items: baseline;
	padding: 8px 12px;
	border-bottom: 1px solid #eef2f7;
	font-size: 13px;
}
.ep-mail-k {
	flex: 0 0 auto;
	color: #8a96a3;
	font-size: 12px;
}
.ep-mail-subject {
	font-weight: 600;
	min-width: 0;
	word-break: break-word;
}
.ep-muted {
	color: #5f6b7a;
}
.ep-mail-paper {
	margin: 12px;
	padding: 20px;
	border-radius: 6px;
	background: #fafbfd;
	border: 1px solid #eef2f7;
	overflow: auto;
}
.ep-mail-title {
	font-size: 18px;
	font-weight: 600;
	color: #1f2d3d;
	margin-bottom: 12px;
	word-break: break-word;
}
.ep-mail-frame {
	display: block;
	width: 100%;
	border: 0;
}
.ep-mail-empty {
	font-size: 14px;
	font-style: italic;
}
.ep-mail-cta {
	margin-top: 16px;
	text-align: center;
}
.ep-mail-button {
	display: inline-block;
	padding: 8px 20px;
	border-radius: 6px;
	background: #01579b;
	color: #fff;
	font-weight: 600;
	font-size: 14px;
}
.ep-mail ::v-deep .ep-ph {
	display: inline-block;
	padding: 0 4px;
	border-radius: 4px;
	background: #e3edfb;
	color: #1a4f9c;
	font-family: monospace;
	font-size: 0.9em;
	font-weight: 600;
	line-height: 1.4;
}
.ep-mail ::v-deep .ep-mail-button .ep-ph {
	background: rgba(255, 255, 255, 0.25);
	color: #fff;
}
.ep-mail ::v-deep .ep-ph--ko {
	background: #fde7e7;
	color: #b3261e;
}
@media (max-width: 768px) {
	.ep-mail-paper {
		margin: 8px;
		padding: 12px;
	}
}
</style>
